import * as React from "react";
import { ChangeEvent, Component } from "react";
import "./autocomplete.scss";
import i18n from "i18next";

interface AutocompleteProps<T> {
  items: T[];

  onChange(item: T): void;

  value?: T;

  matches?(item: T, val: string): boolean;

  toLabel?(item: T): string;

  renderItem?(item: T): JSX.Element | string;

  placeholder?: string;

  prepend?(): string;

  className?: string;
}

interface AutocompleteState<T> {
  textValue?: string;
  focusCount?: number;
  hidden?: boolean;
  suggestions?: T[];
}

function textValue(props: AutocompleteProps<any>) {
  return props.value && props.toLabel ? props.toLabel(props.value) : "";
}

export default class Autocomplete<T> extends Component<AutocompleteProps<T>, AutocompleteState<T>> {
  constructor(props: AutocompleteProps<T>) {
    super(props);

    this.state = {
      textValue: textValue(props),
      focusCount: 0,
      hidden: true,
      suggestions: props.items || [],
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: AutocompleteProps<T>) {
    if (nextProps.value !== this.props.value) {
      this.setState({ textValue: textValue(nextProps) });
    }

    if (nextProps.items !== this.props.items) {
      this.setState({ suggestions: nextProps.items || [] });
    }
  }

  selectItem(item: T) {
    this.setState({
      hidden: true,
      textValue: "",
      suggestions: this.props.items || [],
    });

    this.props.onChange(item);
  }

  resetValue() {
    this.selectItem(null);
  }

  onKeyDown(evt: KeyboardEvent) {
    const keyCode = evt.keyCode;

    // Esc
    if (keyCode === 27)
      this.setState({
        hidden: true,
        textValue: textValue(this.props),
      });

    // Enter
    if (keyCode === 13 && this.state.suggestions.length > 0) this.selectItem(this.state.suggestions[0]);
  }

  onTextValue(evt: ChangeEvent<HTMLInputElement>) {
    evt.preventDefault();

    let val = evt.target.value;
    if (val === null) val = "";

    const matches = (item: T, val: string) => {
      if (typeof this.props.matches === "function") {
        return this.props.matches(item, val);
      } else if (typeof this.props.toLabel === "function") {
        return this.props.toLabel(item).match(new RegExp(val, "i"));
      } else {
        console.warn("No behavior defined to match items");
        return true;
      }
    };

    this.setState({
      textValue: val,
      hidden: false,
      suggestions: this.props.items.filter((item) => matches(item, val)),
    });
  }

  triggerIn() {
    this.setState((prevState) => {
      const tmp = prevState.focusCount + 1;

      const result: AutocompleteState<T> = {
        focusCount: tmp,
        hidden: tmp <= 0,
      };

      if (prevState.focusCount === 0) {
        // Reset input to type
        result.textValue = "";
      }

      return result;
    });
  }

  triggerOut() {
    // Differ trigger out
    setTimeout(
      () =>
        this.setState((prevState) => {
          const tmp = prevState.focusCount - 1;
          const hidden = tmp <= 0;

          const result: any = {
            focusCount: tmp,
            hidden: hidden,
          };

          // Reset the selected value
          if (hidden) {
            result.textValue = textValue(this.props);
          }

          return result;
        }),
      100
    );
  }

  render() {
    const renderItem = this.props.renderItem || this.props.toLabel;

    return (
      <div className={this.props.className ? "autocomplete " + this.props.className : "autocomplete"}>
        {this.props.prepend ? (
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text">{this.props.prepend()}</span>
            </div>

            <input
              type="text"
              className="form-control"
              value={this.state.textValue}
              onChange={(evt) => this.onTextValue(evt)}
              onKeyDown={this.onKeyDown.bind(this)}
              onFocus={this.triggerIn.bind(this)}
              onBlur={this.triggerOut.bind(this)}
              placeholder={this.props.placeholder}
            />
          </div>
        ) : (
          <input
            type="text"
            className="form-control"
            value={this.state.textValue}
            onChange={(evt) => this.onTextValue(evt)}
            onKeyDown={this.onKeyDown.bind(this)}
            onFocus={this.triggerIn.bind(this)}
            onBlur={this.triggerOut.bind(this)}
            placeholder={this.props.placeholder}
          />
        )}

        <div className="autocomplete-list">
          <div className="list-group" hidden={this.state.hidden}>
            {this.state.suggestions.map((suggest, idx) => (
              <button
                type="button"
                key={idx}
                className="list-group-item list-group-item-action"
                onClick={(evt) => this.selectItem(suggest)}
                onFocus={this.triggerIn.bind(this)}
                onBlur={this.triggerOut.bind(this)}
              >
                {renderItem(suggest)}
              </button>
            ))}

            <button
              type="button"
              className="list-group-item list-group-item-action"
              onClick={this.resetValue.bind(this)}
              onFocus={this.triggerIn.bind(this)}
              onBlur={this.triggerOut.bind(this)}
            >
              {i18n.t("global.delete") + " ..."}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
